import { CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CountrySelector } from '@/components/ui/country-selector';
import { BankNameSelect } from '@/components/banking/BankNameSelect';
import { IbanInput, type IbanStatus } from '@/components/banking/IbanInput';
import type { BankCodeOption } from '@/hooks/react-query/useBankCodes';
import type { IbanValidation } from '@/types/banking';

export type { IbanStatus };
export type SwiftStatus = 'idle' | 'validating' | 'valid' | 'invalid';

interface InvestorBankingFormProps {
  formData: {
    iban: string;
    account_number: string;
    bank_name: string;
    bank_account_name: string;
    swift_bic: string;
    bank_address: string;
    bank_country: string;
  };
  ibanStatus: IbanStatus;
  swiftStatus: SwiftStatus;
  bankNameLocked: boolean;
  accountNumberError?: string;
  bankNameError?: string;
  bankCountryError?: string;
  onInputChange: (field: string, value: string) => void;
  onBankSelect: (bank: BankCodeOption) => void;
  onValidateIban: (iban: string) => Promise<IbanValidation>;
  onIbanValidated: (validation: IbanValidation) => void;
  onIbanHydrated: (validation: IbanValidation) => void;
  onIbanStatusChange: (status: IbanStatus) => void;
  onSwiftBlur: (value: string) => void;
}

// Border colour reflecting an idle/validating/valid/invalid status.
function statusBorder(status: SwiftStatus): string {
  switch (status) {
    case 'valid':
      return 'border-success';
    case 'validating':
      return 'border-warning';
    case 'invalid':
      return 'border-destructive';
    default:
      return '';
  }
}

// Trailing spinner / tick / cross reflecting the validation status.
function StatusAdornment({ status }: { status: SwiftStatus }) {
  if (status === 'validating') {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-900" />
      </div>
    );
  }
  if (status === 'valid' || status === 'invalid') {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {status === 'valid' ? (
          <span className="text-success">✓</span>
        ) : (
          <span className="text-destructive">✗</span>
        )}
      </div>
    );
  }
  return null;
}

export function InvestorBankingForm({
  formData,
  ibanStatus,
  swiftStatus,
  bankNameLocked,
  accountNumberError,
  bankNameError,
  bankCountryError,
  onInputChange,
  onBankSelect,
  onValidateIban,
  onIbanValidated,
  onIbanHydrated,
  onIbanStatusChange,
  onSwiftBlur,
}: InvestorBankingFormProps) {
  // An IBAN account is the anchor when the IBAN validates; otherwise the form
  // falls back to a SWIFT/BIC-anchored account (account number + SWIFT).
  const hasValidIban = ibanStatus === 'valid';
  // Banking is optional, so nothing is marked required until the user starts
  // filling it in. From then on the account needs an identifier - an IBAN or an
  // account number - and a non-IBAN account also has to spell out its bank.
  const hasIbanText = formData.iban.trim().length > 0;
  const hasAccountNumber = formData.account_number.trim().length > 0;
  const bankingStarted =
    hasIbanText || hasAccountNumber || formData.swift_bic.trim().length > 0;
  const needsIdentifier = bankingStarted && !hasAccountNumber;
  const needsBankDetails = bankingStarted && !hasIbanText;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <CreditCard className="h-5 w-5" />
          <span>Banking Details</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* IBAN - always shown; the primary anchor */}
          <div className="space-y-2">
            <Label htmlFor="iban">IBAN{needsIdentifier && ' *'}</Label>
            <IbanInput
              id="iban"
              value={formData.iban}
              onChange={(value) => onInputChange('iban', value)}
              validate={onValidateIban}
              onValidated={onIbanValidated}
              onHydrated={onIbanHydrated}
              onStatusChange={onIbanStatusChange}
              placeholder="e.g. NL91 ABNA 0417 1643 00"
            />
            {ibanStatus === 'invalid' ? (
              <p className="text-sm text-destructive">
                Not a valid IBAN. Clear it to enter an account number + SWIFT
                instead.
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                Have an IBAN? Enter it to auto-fill the rest. No IBAN (e.g. US
                accounts)? Leave blank and fill the details below.
              </p>
            )}
          </div>

          {/* Account holder - always shown */}
          <div className="space-y-2">
            <Label htmlFor="bank_account_name">Account Holder Name</Label>
            <Input
              id="bank_account_name"
              value={formData.bank_account_name}
              onChange={(e) =>
                onInputChange('bank_account_name', e.target.value)
              }
              placeholder="Account holder name"
            />
          </div>

          {/* Account number - revealed only when there is no valid IBAN */}
          {!hasValidIban && (
            <div className="space-y-2">
              <Label htmlFor="account_number">
                Account Number{needsBankDetails && ' *'}
              </Label>
              <Input
                id="account_number"
                value={formData.account_number}
                onChange={(e) =>
                  onInputChange('account_number', e.target.value)
                }
                placeholder="e.g. 000123456789"
                className={accountNumberError ? 'border-destructive' : ''}
              />
              {accountNumberError ? (
                <p className="text-sm text-destructive">{accountNumberError}</p>
              ) : (
                <p className="text-sm text-gray-500">
                  For accounts without an IBAN.
                </p>
              )}
            </div>
          )}

          {/* SWIFT/BIC - auto-filled from IBAN, editable; the anchor without one */}
          <div className="space-y-2">
            <Label htmlFor="swift_bic">SWIFT/BIC Code</Label>
            <div className="relative">
              <Input
                id="swift_bic"
                value={formData.swift_bic}
                onChange={(e) => onInputChange('swift_bic', e.target.value)}
                onBlur={(e) => onSwiftBlur(e.target.value)}
                placeholder="e.g. USBKUS44IMT"
                className={statusBorder(hasValidIban ? 'idle' : swiftStatus)}
              />
              {!hasValidIban && <StatusAdornment status={swiftStatus} />}
            </div>
            <p className="text-sm text-gray-500">
              {hasValidIban
                ? 'Auto-filled from the IBAN - edit if the bank isn’t recognised.'
                : 'Helps route a payment to an account without an IBAN.'}
            </p>
          </div>

          {/* Bank name - registry-only searchable dropdown, read-only on IBAN match */}
          <div className="space-y-2">
            <Label htmlFor="bank_name">
              Bank Name{needsBankDetails && ' *'}
            </Label>
            <BankNameSelect
              value={formData.bank_name}
              countryCode={formData.bank_country}
              locked={bankNameLocked}
              className={bankNameError ? 'border-destructive' : ''}
              onSelect={onBankSelect}
            />
            {bankNameError ? (
              <p className="text-sm text-destructive">{bankNameError}</p>
            ) : (
              <p className="text-sm text-gray-500">
                {bankNameLocked
                  ? 'Matched from the IBAN and managed in the bank registry.'
                  : 'Search the bank registry and select the bank.'}
              </p>
            )}
          </div>

          {/* Bank country - derived from the IBAN/SWIFT, editable when there's
              no IBAN to drive it (e.g. US accounts) */}
          <div className="space-y-2">
            <Label htmlFor="bank_country">
              Bank Country{needsBankDetails && ' *'}
            </Label>
            <CountrySelector
              id="bank_country"
              value={formData.bank_country}
              onValueChange={(value) => onInputChange('bank_country', value)}
              placeholder="Select bank country"
              disabled={hasValidIban}
              className={bankCountryError ? 'border-destructive' : ''}
            />
            {bankCountryError ? (
              <p className="text-sm text-destructive">{bankCountryError}</p>
            ) : (
              <p className="text-sm text-gray-500">
                {hasValidIban
                  ? 'Set from the IBAN.'
                  : 'Detected from the SWIFT/BIC - set it manually if needed.'}
              </p>
            )}
          </div>

          {/* Bank address - needed for SWIFT wires to a non-IBAN account */}
          {!hasValidIban && (
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="bank_address">Bank Address</Label>
              <Textarea
                id="bank_address"
                value={formData.bank_address}
                onChange={(e) => onInputChange('bank_address', e.target.value)}
                placeholder="Bank branch address (for international wires)"
                rows={2}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
